import { SpanStatusCode } from '@opentelemetry/api';
import { IDeviceConfigurationDictionary } from 'src/device-status/interfaces/IDeviceConfigurationDictionary';
import { emeraldInitializePayment } from 'src/devices/payment/payment-controller';
import { initializePaymentService } from 'src/devices/payment/pinpad-operations';
import { hasRpsPinpadDevice } from 'src/devices/payment/pinpad-utils';
import { addDevices } from '../../device-status/business-logic/add-device';
import { initializePeripheralConnection } from '../../devices/rds-connection';
import { popupMessage } from '../../messages/business-logic/popup-message';
import { DeviceType } from '../enumerations/device-type';
import { EmeraldDialogTypes } from '../enumerations/emerald-dialog-types';
import XLRLogger from '../helpers/logger';
import { createSpan, endSpan } from '../helpers/tracing-helper';
import { store } from '../redux/configure-store';
import { DeviceStatusActions } from '../redux/reducers/device-status-reducer';

export const DEVICE_CHECK_INTERVAL = 200;

export const initializeDevices = async (startTouchpoint?: boolean): Promise<boolean> => {
  const span = createSpan('[RDS] initialize-devices', undefined, true, true);

  const deviceList: IDeviceConfigurationDictionary[] | undefined = store.getState().touchPoint
    .touchPointConfiguration.deviceList;
  const customerDisplay = deviceList?.find(d => d.LogicalName === 'CustomerDisplay');
  let showCustomerDisplay = false;

  if (customerDisplay !== undefined) {
    showCustomerDisplay = true;
  }

  if (!deviceList) {
    span.setStatus({
      code: SpanStatusCode.OK,
      message: 'Empty device list',
    });
    endSpan(span);
    return true;
  }
  /* ZH -
   * addDevices populates the device list in Redux based upon backoffice configuration parameters.
   */
  addDevices(deviceList, showCustomerDisplay);
  try {
    const peripheralDevices: IDeviceConfigurationDictionary[] = [];

    deviceList.forEach(d => {
      const deviceType = getDeviceType(d.Type);
      if (deviceType === DeviceType.CustomerDisplay) {
        updateDeviceStatus({
          deviceName: d.LogicalName ?? '',
          initialized: true,
          deviceType: DeviceType.CustomerDisplay,
        });
      } else if (deviceType !== DeviceType.CCL) {
        peripheralDevices.push(d);
      }
    });

    if (peripheralDevices.length) {
      try {
        await initializePeripheralConnection(peripheralDevices, span.spanContext());
      } catch (err) {
        XLRLogger.error({
          message: 'Error initializing peripheral devices',
          source: 'initialize-devices',
          data: { error: err },
        });
        if (!startTouchpoint) {
          throw err;
        }
      }
    }

    if (hasRpsPinpadDevice()) {
      const span = createSpan('[RPS] initiate-payment', undefined, true, true);
      if (window.Cypress) {
        await initializePaymentService(span.spanContext());
        endSpan(span);
      } else {
        await emeraldInitializePayment(span.spanContext())
          .then(() => initializePaymentService(span.spanContext()))
          .then(() => endSpan(span));
      }
    }
  } catch (err) {
    if (!startTouchpoint) {
      popupMessage({ message: 'DeviceLoadMessage', type: EmeraldDialogTypes.Warning });
    }
  }
  endSpan(span);
  return isInitialized();
};

export const updateDeviceStatus = (device: InitializedDevice) => {
  const { deviceName, deviceType, initialized } = device;
  store.dispatch(
    DeviceStatusActions.updateDevice({
      logicalName: deviceName,
      warning: !initialized,
      initialized,
      deviceType,
    }),
  );
};

export const getDeviceType = (deviceTypeString?: string): DeviceType => {
  if (!deviceTypeString) {
    return DeviceType.Unknown;
  }

  const normalizedDevice = deviceTypeString.toLowerCase();

  if (normalizedDevice.indexOf('scanner') >= 0) {
    return DeviceType.Scanner;
  }

  if (normalizedDevice.indexOf('printer') >= 0) {
    return DeviceType.Printer;
  }

  if (normalizedDevice.indexOf('ccl') >= 0) {
    return DeviceType.CCL;
  }

  if (normalizedDevice.indexOf('drawer') >= 0) {
    return DeviceType.CashDrawer;
  }

  if (normalizedDevice.indexOf('scale') >= 0) {
    return DeviceType.Scale;
  }

  if (normalizedDevice.indexOf('micr') >= 0) {
    return DeviceType.MICR;
  }

  if (normalizedDevice.indexOf('customerdisplay') >= 0) {
    return DeviceType.CustomerDisplay;
  }

  return DeviceType.Unknown;
};

const isInitialized = () => {
  return store.getState().deviceStatus.devices.every(device => device.initialized);
};

export interface InitializedDevice {
  initialized: boolean;
  deviceName: string;
  deviceType: DeviceType;
}
